import { Avatar, Badge, Box, styled, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import LinearProgress from '@mui/material/LinearProgress';
import DiamondIcon from '@mui/icons-material/Diamond';
import { paths } from '@common/types';
import { capitalize } from 'lodash';
import { theme } from '@store';

interface ITeamAvailabilityHeaderProps {
  teamName: string;
  activeCount: number;
  totalCount: number;
  color: string;
}

export const TeamAvailabilityHeader = ({
  activeCount,
  totalCount,
  teamName,
  color,
}: ITeamAvailabilityHeaderProps) => {
  const capacityValue = (activeCount * 100) / (totalCount || 1);
  const capacity = capacityValue.toFixed(1);

  return (
    <Box
      sx={{
        display: 'flex',
        position: 'relative',
        flexDirection: 'column',
        width: '100%',
        padding: '0',
        mb: '1rem',
        px: '1.5rem',
        gap: '1rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'start',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Team avatar here */}
        <DiamondIcon
          sx={{
            width: 55,
            height: 50,
            marginRight: 1,
            color: color,
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='body1'>{teamName}</Typography>
          <Typography
            variant='body2'
            sx={{ color: theme => theme.palette.semiBlack.main }}
          >
            Team
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: theme => theme.palette.semiBlack.main }}
        >
          Team availability:
        </Typography>
        <Typography variant='subtitle2'>
          {`${activeCount} out of ${totalCount}`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress
            color='primary'
            variant='determinate'
            sx={{ height: '1rem' }}
            value={capacityValue}
          />
        </Box>
        <Box>
          <Typography
            variant='body2'
            sx={{ color: 'text.secondary' }}
          >{`${capacity}%`}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const StyledBadge = styled(Badge)(({ theme, color }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: color,
    color: color,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
    },
  },
}));

const getAvatarStatusColor = (
  status: paths[`/user/{user_id}`]['get']['responses']['200']['content']['application/json']['activity_status'],
) => {
  switch (status) {
    case 'active':
      return theme.palette.secondary.main;
    case 'away':
      return theme.palette.goldenYellow.main;
    case 'offline':
    default:
      return theme.palette.grey[500];
  }
};

interface IUserEntryProps {
  user: paths[`/team/{team_id}`]['get']['responses']['200']['content']['application/json']['users'][0];
}

export const UserEntry = ({ user }: IUserEntryProps) => {
  const {
    first_name,
    last_name,
    role,
    lead: isLeader,
    activity_status,
    suffix,
  } = user;
  const name = `${first_name} ${last_name}`;
  const thumbnail = user.image?.thumbnail_xsmall;
  const isDisabled = user.activity_status === 'offline';

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        maxWidth: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <StyledBadge
            overlap='circular'
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            color={getAvatarStatusColor(activity_status) as any}
            variant='dot'
            sx={{
              height: '100%',
            }}
          >
            {thumbnail ? (
              <Avatar
                src={thumbnail}
                sx={{
                  width: '40px',
                  height: '40px',
                  filter: isDisabled
                    ? 'grayscale(70%) brightness(100%)'
                    : 'none',
                }}
                alt='user-logo'
              />
            ) : (
              <Avatar>{first_name[0] + last_name[0]}</Avatar>
            )}
          </StyledBadge>

          <Box sx={{ display: 'flex', flexDirection: 'column', pl: '1rem' }}>
            <Typography
              variant='body1'
              sx={{
                color: isDisabled ? 'text.disabled' : 'text.primary',
              }}
            >
              {`${name} ${suffix ? suffix : ''}`}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: isDisabled
                  ? 'text.disabled'
                  : theme => theme.palette.semiBlack.main,
              }}
            >
              {role || '-'}
              {role && isLeader ? ', Team lead' : ''}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme => theme.palette.semiBlack.main }}
          >
            {capitalize(activity_status)}
          </Typography>
          {isLeader && (
            <StarIcon
              sx={{
                color: isDisabled
                  ? theme => theme.palette.action.disabled
                  : 'primary.main',
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
